import React, { useState, useEffect } from 'react';
import { Heading, Grid, View, Loader, Button, Table, TableBody, TableCell, TableRow } from '@aws-amplify/ui-react';
import classNames from "classnames";
import { Line, Bar } from "react-chartjs-2";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
} from "reactstrap";
import { useParams, useNavigate } from 'react-router-dom';
import { fetchCampaignData, getLastFortnight, getFortnightPeriods } from './Dev';
import { SidePopout } from './SidePopout';
import './css/dashboard.css';
import './css/table.css';
import './css/Graphs.css';


function sortByKey(array, key) {
    return array.sort((a, b) => {
        // Use bracket notation to access the value of the key dynamically
        const valueA = a[key];
        const valueB = b[key];
        // Compare the values
        if (valueA < valueB) {
            return -1;
        }
        if (valueA > valueB) {
            return 1;
        }
        return 0;
    });
}


const CampaignPage = ({ company }) => {
    const navigate = useNavigate();
    const [campaignData, setCampaignData] = useState(undefined);
    const { campaignId } = useParams();
    const lastFortnight = getLastFortnight();
    const fortnights = getFortnightPeriods(lastFortnight).reverse();
    const [isPopoutOpen, setIsPopoutOpen] = useState(false);

    const handleOpenPopout = () => {
        setIsPopoutOpen(true);
    };

    const handleClosePopout = () => {
        setIsPopoutOpen(false);
    }

    useEffect(() => {
        const getCampaignData = async () => {
            try {
                const newCampaign = await fetchCampaignData(campaignId);
                setCampaignData(sortByKey(newCampaign, 'date'));
            } catch (error) {
                console.error('Error fetching campaign data:', error);
            }
        };

        getCampaignData();
    }, [campaignId]);

    if (!campaignData) {
        return <div><Loader size="large"/></div>;
    }

    if (campaignData === []) {
        return (
            <View>
                <Heading level={1} color="#b9b5b2" style={{ margin: '0 auto' }}>Campaign {campaignId} not found.</Heading>
                <Button
                    variation="primary"
                    colorTheme="warning"
                    onClick={() => navigate('/dashboard')}
                >
                    Back to Dashboard
                </Button>
            </View>
        )
    }

    const campaign_type = campaignData[campaignData.length - 1]['adType'].replace("_", " ");

    if (campaign_type === "sponsored products") {
        const fortnights = campaignData.map(item => item.date)
        const campaign_name = campaignData[campaignData.length - 1]['campaignName'];
        const campaign_status = campaignData[campaignData.length - 1]['campaignStatus'];
        let campaign_region = "";
        let budget_amount = "";

        if (campaignData[campaignData.length - 1]['campaignBudgetCurrencyCode'] === "EUR") {
            campaign_region = "🇪🇺";
            budget_amount = "€".concat(campaignData[campaignData.length - 1]['campaignBudgetAmount']);
        } else if (campaignData[campaignData.length - 1]['campaignBudgetCurrencyCode'] === "USD") {
            campaign_region = "🇺🇸";
            budget_amount = "$".concat(campaignData[campaignData.length - 1]['campaignBudgetAmount']);
        } else if (campaignData[campaignData.length - 1]['campaignBudgetCurrencyCode'] === "GBP") {
            campaign_region = "🇬🇧";
            budget_amount = "£".concat(campaignData[campaignData.length - 1]['campaignBudgetAmount']);
        }


        const budget_type = campaignData[campaignData.length - 1]['campaignBudgetType'].split("_")[0];

        const spend_8 = campaignData.map(item => item.spend).slice(-8).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const spend_8_avg = (spend_8 / 8).toFixed(2);
        
        const spend_4 = campaignData.map(item => item.spend).slice(-4).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const spend_4_avg = (spend_4 / 4).toFixed(2);
        const spend_2 = campaignData.map(item => item.spend).slice(-2).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const spend_2_avg = (spend_2 / 2).toFixed(2);
        
        const sales_8 = campaignData.map(item => item.sales1d).slice(-8).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const sales_8_avg = (sales_8 / 8).toFixed(2);
        
        const sales_4 = campaignData.map(item => item.sales1d).slice(-4).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const sales_4_avg = (sales_4 / 4).toFixed(2);
        const sales_2 = campaignData.map(item => item.sales1d).slice(-2).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const sales_2_avg = (sales_2 / 2).toFixed(2);
        
        let acos_8 = 0;
        if (spend_8 > 0 && sales_8 > 0) {
            acos_8 = ((spend_8 / sales_8) * 100).toFixed(2);
        } else if (spend_8 > 0) {
            acos_8 = "> 100";
        }
        let acos_4 = 0;
        if (spend_4 > 0 && sales_4 > 0) {
            acos_4 = ((spend_4 / sales_4) * 100).toFixed(2);
        } else if (spend_4 > 0) {
            acos_4 = "> 100";
        }
        let acos_2 = 0;
        if (spend_2 > 0 && sales_2 > 0) {
            acos_2 = ((spend_2 / sales_2) * 100).toFixed(2);
        } else if (spend_2 > 0) {
            acos_2 = "> 100";
        }

        const impressions_8 = campaignData.map(item => item.impressions).slice(-8).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const impressions_8_avg = (impressions_8 / 8).toFixed(0);
        const impressions_4 = campaignData.map(item => item.impressions).slice(-4).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const impressions_4_avg = (impressions_4 / 4).toFixed(0);
        const impressions_2 = campaignData.map(item => item.impressions).slice(-2).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const impressions_2_avg = (impressions_2 / 2).toFixed(0);

        const clicks_8 = campaignData.map(item => item.clicks).slice(-8).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const clicks_8_avg = (clicks_8 / 8).toFixed(0);
        const clicks_4 = campaignData.map(item => item.clicks).slice(-4).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const clicks_4_avg = (clicks_4 / 4).toFixed(0);
        const clicks_2 = campaignData.map(item => item.clicks).slice(-2).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const clicks_2_avg = (clicks_2 / 2).toFixed(0);


        const purchases_8 = campaignData.map(item => item.purchases1d).slice(-8).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const purchases_8_avg = (purchases_8 / 8).toFixed(0);
        const purchases_4 = campaignData.map(item => item.purchases1d).slice(-4).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const purchases_4_avg = (purchases_4 / 4).toFixed(0);
        const purchases_2 = campaignData.map(item => item.purchases1d).slice(-2).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const purchases_2_avg = (purchases_2 / 2).toFixed(0);

        let cpc_8 = 0;
        let conversion_8 = 0;
        if (clicks_8 > 0) {
            cpc_8 = (spend_8 / clicks_8).toFixed(2);
            conversion_8 = ((purchases_8 / clicks_8) * 100).toFixed(0);
        } else {
            cpc_8 = spend_8;
        }
        let cpc_4 = 0;
        let conversion_4 = 0;
        if (clicks_4 > 0) {
            cpc_4 = (spend_4 / clicks_4).toFixed(2);
            conversion_4 = ((purchases_4 / clicks_4) * 100).toFixed(0);
        } else {
            cpc_4 = spend_4;
        }
        let cpc_2 = 0;
        let conversion_2 = 0;
        if (clicks_2 > 0) {
            cpc_2 = (spend_2 / clicks_2).toFixed(2);
            conversion_2 = ((purchases_2 / clicks_2) * 100).toFixed(0);
        } else {
            cpc_2 = spend_2;
        }
        

        let chart_options = {
            maintainAspectRatio: true,
            legend: {
                display: false,
            },
            tooltips: {
                backgroundColor: "#f5f5f5",
                titleFontColor: "#333",
                bodyFontColor: "#666",
                bodySpacing: 4,
                xPadding: 12,
                mode: "nearest",
                intersect: 10,
                position: "nearest",
                callbacks: {
                    label: (tooltipItem, data) => {
                        const label = data.datasets[tooltipItem.datasetIndex].label || '';
                        const value = tooltipItem.yLabel || '';

                        const dateRange = fortnights[tooltipItem.index] || '';
                        return `${label}: ${value} (${dateRange})`;
                    },
                },
            },
            responsive: true,
            scales: {
                yAxes: {
                    barPercentage: 1.6,
                    gridLines: {
                        drawBorder: false,
                        color: "rgba(29,140,248,0.0)",
                        zeroLineColor: "transparent",
                    },
                    ticks: {
                        padding: 20,
                        fontColor: "#9a9a9a",
                    },
                },
                xAxes: {
                    barPercentage: 1.6,
                    gridLines: {
                        drawBorder: false,
                        color: "rgba(29,140,248,0.1)",
                        zeroLineColor: "transparent",
                    },
                    ticks: {
                        padding: 20,
                        fontColor: "#9a9a9a", 
                        // callback: (value, index, values) => {
                        //     return value - fortnights.length; // Count down to -10 as leftmost point
                        // },
                    },
                },
            },
        };


        let impressions_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => item.impressions) || [],
                        },
                        {
                            label: "Last 8 week avg.",
                            fill: false,
                            borderColor: "#89949F", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => impressions_8_avg) || [],
                        },
                        {
                            label: "Last 4 week Avg.",
                            fill: false,
                            borderColor: "#5C6670", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => impressions_4_avg) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let clicks_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => item.clicks) || [],
                        },
                        {
                            label: "Last 8 week Avg.",
                            fill: false,
                            borderColor: "#89949F", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => clicks_8_avg) || [],
                        },
                        {
                            label: "Last 4 week Avg.",
                            fill: false,
                            borderColor: "#5C6670", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => clicks_4_avg) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let purchases_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => item.purchases1d) || [],
                        },
                        {
                            label: "Last 8 week Avg.",
                            fill: false,
                            borderColor: "#89949F", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => purchases_8_avg) || [],
                        },
                        {
                            label: "Last 4 week Avg.",
                            fill: false,
                            borderColor: "#5C6670", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => purchases_4_avg) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let spend_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => item.spend) || [],
                        },
                        {
                            label: "Last 8 week Avg.",
                            fill: false,
                            borderColor: "#89949F", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => spend_8_avg) || [],
                        },
                        {
                            label: "Last 4 week Avg.",
                            fill: false,
                            borderColor: "#5C6670", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => spend_4_avg) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let CPC_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => {
                                if (item.clicks !== 0) {
                                    return item.cost / item.clicks;
                                } else {
                                    return item.cost;
                                }
                            }) || [],
                        },
                        {
                            label: "Last 8 week Avg.",
                            fill: false,
                            borderColor: "#89949F", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => cpc_8) || [],
                        },
                        {
                            label: "Last 4 week Avg.",
                            fill: false,
                            borderColor: "#5C6670", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => cpc_4) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let spend_per_purchase_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => {
                                if (item.purchases1d !== 0) {
                                    return item.spend / item.purchases1d;
                                } else {
                                    return item.spend;
                                }
                            }) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };

        let acos_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => {
                                if (item.sales1d !== 0) {
                                    return (item.spend / item.sales1d) * 100;
                                } else {
                                    return null;
                                }
                            }) || [],
                        },
                        {
                            label: "Last 8 week Avg.",
                            fill: false,
                            borderColor: "#89949F", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => acos_8) || [],
                        },
                        {
                            label: "Last 4 week Avg.",
                            fill: false,
                            borderColor: "#5C6670", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => acos_4) || [],
                        },
                        {
                            label: "Profitability",
                            fill: false,
                            borderColor: "#a9b665", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => 20) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let sales_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => item.sales1d) || [],
                        },
                        {
                            label: "Last 8 week Avg.",
                            fill: false,
                            borderColor: "#89949F", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => sales_8_avg) || [],
                        },
                        {
                            label: "Last 4 week Avg.",
                            fill: false,
                            borderColor: "#5C6670", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => sales_4_avg) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let ctr_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => item.clickThroughRate) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let conversion_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => {
                                if (item.clicks !== 0) {
                                    return (item.purchases1d / item.clicks) * 100;
                                } else {
                                    return null;
                                }
                            }) || [],
                        },
                        {
                            label: "Last 8 week Avg.",
                            fill: false,
                            borderColor: "#89949F", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => conversion_8) || [],
                        },
                        {
                            label: "Last 4 week Avg.",
                            fill: false,
                            borderColor: "#5C6670", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => conversion_4) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let top_of_search_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => item.topOfSearchImpressionShare) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        return (
            <div>
                <div style={{ marginBottom: '2.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Button
                        variation="primary"
                        colorTheme="warning"
                        onClick={() => navigate('/sp')}
                        style={{ marginRight: 'auto' }}
                    >
                        &lt; Campaign List
                    </Button>
                    <Heading level={1} color="#b9b5b2" style={{ margin: '0 auto' }}>{campaign_name} ({campaignId})</Heading>
                    <Button onClick={handleOpenPopout}>✍️</Button>
                    <SidePopout 
                        company={company}
                        campaigns={[`${campaign_name} - ${campaignId}`]}
                        dates={fortnights}
                        isOpen={isPopoutOpen}
                        onClose={handleClosePopout}
                    />
                </div>
                <Grid
                    columnGap="2rem"
                    rowGap="0.5rem"
                    templateColumns="1fr 3fr"
                    templateRows="1fr"
                >
                    <View>
                        <Grid 
                            columnGap="2rem"
                            rowGap="0.5rem"
                            templateColumns="1fr 1fr"
                            templateRows="1fr"
                        >
                            <View style={{ textAlign: 'left' }}>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>Status:</Heading>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>Type:</Heading>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>Region:</Heading>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>Budget Amount:</Heading>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>Budget Type:</Heading> 
                            </View>
                            <View style={{ textAlign: 'right' }}>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>{campaign_status}</Heading>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>{campaign_type}</Heading>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>{campaign_region}</Heading>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>{budget_amount}</Heading>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>{budget_type}</Heading> 
                            </View>
                        </Grid>
                        <Table
                            highlightOnHover={true}
                            className="global-styling-table"
                        >
                        <TableBody>
                            <TableRow>
                              <TableCell as="th" />
                              <TableCell as="th">Last 8 week avg.</TableCell>
                              <TableCell as="th">Last 4 week avg.</TableCell>
                              <TableCell as="th">Last 2 week avg.</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell as="th">ACOS</TableCell>
                              <TableCell>{acos_8} %</TableCell>
                              <TableCell>{acos_4} %</TableCell>
                              <TableCell>{acos_2} %</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell as="th">CPC</TableCell>
                              <TableCell>{cpc_8}</TableCell>
                              <TableCell>{cpc_4}</TableCell>
                              <TableCell>{cpc_2}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell as="th">Spend</TableCell>
                              <TableCell>{spend_8_avg}</TableCell>
                              <TableCell>{spend_4_avg}</TableCell>
                              <TableCell>{spend_2_avg}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell as="th">Sales</TableCell>
                              <TableCell>{sales_8_avg}</TableCell>
                              <TableCell>{sales_4_avg}</TableCell>
                              <TableCell>{sales_2_avg}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell as="th">Impressions</TableCell>
                              <TableCell>{impressions_8_avg}</TableCell>
                              <TableCell>{impressions_4_avg}</TableCell>
                              <TableCell>{impressions_2_avg}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell as="th">Clicks</TableCell>
                              <TableCell>{clicks_8_avg}</TableCell>
                              <TableCell>{clicks_4_avg}</TableCell>
                              <TableCell>{clicks_2_avg}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell as="th">Purchases</TableCell>
                              <TableCell>{purchases_8_avg}</TableCell>
                              <TableCell>{purchases_4_avg}</TableCell>
                              <TableCell>{purchases_2_avg}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell as="th">Conversion</TableCell>
                              <TableCell>{conversion_8} %</TableCell>
                              <TableCell>{conversion_4} %</TableCell>
                              <TableCell>{conversion_2} %</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                    </View>
                    <View className="graph-container">
                        <Grid
                            columnGap="0.5rem"
                            rowGap="0.5rem"
                            templateColumns="1fr 1fr"
                            templateRows="1fr 1fr 1fr 1fr 1fr"
                        >
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Impressions</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={impressions_graph.data}
                                            options={impressions_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Clicks</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={clicks_graph.data}
                                            options={clicks_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Purchases</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={purchases_graph.data}
                                            options={purchases_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">ACOS</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={acos_graph.data}
                                            options={acos_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Spend</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={spend_graph.data}
                                            options={spend_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Sales Revenue</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={sales_graph.data}
                                            options={sales_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Spend Per Click</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={CPC_graph.data}
                                            options={CPC_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Spend Per Purchase</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={spend_per_purchase_graph.data}
                                            options={spend_per_purchase_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Click Through Rate</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={ctr_graph.data}
                                            options={ctr_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Conversion Rate</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={conversion_graph.data}
                                            options={conversion_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Top of Search %</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={top_of_search_graph.data}
                                            options={top_of_search_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                        </Grid>        
                    </View>
                </Grid>
            </div>
        )
    } else if (campaign_type === "sponsored brands") {
        const fortnights = campaignData.map(item => item.date)
        const campaign_name = campaignData[campaignData.length - 1]['campaignName'];
        const campaign_status = campaignData[campaignData.length - 1]['campaignStatus'];
        let campaign_region = "";
        let budget_amount = "";

        if (campaignData[campaignData.length - 1]['campaignBudgetCurrencyCode'] === "EUR") {
            campaign_region = "🇪🇺";
            budget_amount = "€".concat(campaignData[campaignData.length - 1]['campaignBudgetAmount']);
        } else if (campaignData[campaignData.length - 1]['campaignBudgetCurrencyCode'] === "USD") {
            campaign_region = "🇺🇸";
            budget_amount = "$".concat(campaignData[campaignData.length - 1]['campaignBudgetAmount']);
        } else if (campaignData[campaignData.length - 1]['campaignBudgetCurrencyCode'] === "GBP") {
            campaign_region = "🇬🇧";
            budget_amount = "£".concat(campaignData[campaignData.length - 1]['campaignBudgetAmount']);
        }


        const budget_type = campaignData[campaignData.length - 1]['campaignBudgetType'].split("_")[0];
        const cost_type = campaignData[campaignData.length - 1]['costType'];

        const spend_8 = campaignData.map(item => item.cost).slice(-8).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const spend_8_avg = (spend_8 / 8).toFixed(2);
        const spend_4 = campaignData.map(item => item.cost).slice(-4).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const spend_4_avg = (spend_4 / 4).toFixed(2);
        const spend_2 = campaignData.map(item => item.cost).slice(-2).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const spend_2_avg = (spend_2 / 2).toFixed(2);
        
        const sales_8 = campaignData.map(item => item.salesClicks).slice(-8).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const sales_8_avg = (sales_8 / 8).toFixed(2);
        const sales_4 = campaignData.map(item => item.salesClicks).slice(-4).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const sales_4_avg = (sales_4 / 4).toFixed(2);
        const sales_2 = campaignData.map(item => item.salesClicks).slice(-2).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const sales_2_avg = (sales_2 / 2).toFixed(2);
        
        let acos_8 = 0;
        if (spend_8 > 0 && sales_8 > 0) {
            acos_8 = ((spend_8 / sales_8) * 100).toFixed(2);
        } else if (spend_8 > 0) {
            acos_8 = "> 100";
        }
        let acos_4 = 0;
        if (spend_4 > 0 && sales_4 > 0) {
            acos_4 = ((spend_4 / sales_4) * 100).toFixed(2);
        } else if (spend_4 > 0) {
            acos_4 = "> 100";
        }
        let acos_2 = 0;
        if (spend_2 > 0 && sales_2 > 0) {
            acos_2 = ((spend_2 / sales_2) * 100).toFixed(2);
        } else if (spend_2 > 0) {
            acos_2 = "> 100";
        }

        const impressions_8 = campaignData.map(item => item.viewableImpressions).slice(-8).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const impressions_8_avg = (impressions_8 / 8).toFixed(0);
        const impressions_4 = campaignData.map(item => item.viewableImpressions).slice(-4).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const impressions_4_avg = (impressions_4 / 4).toFixed(0);
        const impressions_2 = campaignData.map(item => item.viewableImpressions).slice(-2).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const impressions_2_avg = (impressions_2 / 2).toFixed(0);

        const clicks_8 = campaignData.map(item => item.clicks).slice(-8).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const clicks_8_avg = (clicks_8 / 8).toFixed(0);
        const clicks_4 = campaignData.map(item => item.clicks).slice(-4).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const clicks_4_avg = (clicks_4 / 4).toFixed(0);
        const clicks_2 = campaignData.map(item => item.clicks).slice(-2).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const clicks_2_avg = (clicks_2 / 2).toFixed(0);


        const add_to_cart_8 = campaignData.map(item => item.addToCartClicks).slice(-8).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const add_to_cart_8_avg = (add_to_cart_8 / 8).toFixed(0);
        const add_to_cart_4 = campaignData.map(item => item.addToCartClicks).slice(-4).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const add_to_cart_4_avg = (add_to_cart_4 / 4).toFixed(0);
        const add_to_cart_2 = campaignData.map(item => item.addToCartClicks).slice(-2).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const add_to_cart_2_avg = (add_to_cart_2 / 2).toFixed(0);


        const purchases_8 = campaignData.map(item => item.purchasesClicks).slice(-8).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const purchases_8_avg = (purchases_8 / 8).toFixed(0);
        const purchases_4 = campaignData.map(item => item.purchasesClicks).slice(-4).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const purchases_4_avg = (purchases_4 / 4).toFixed(0);
        const purchases_2 = campaignData.map(item => item.purchasesClicks).slice(-2).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const purchases_2_avg = (purchases_2 / 2).toFixed(0);

        let cpc_8 = 0;
        let conversion_8 = 0;
        if (clicks_8 > 0) {
            cpc_8 = (spend_8 / clicks_8).toFixed(2);
            conversion_8 = ((purchases_8 / clicks_8) * 100).toFixed(0);
        } else {
            cpc_8 = spend_8;
        }
        let cpc_4 = 0;
        let conversion_4 = 0;
        if (clicks_4 > 0) {
            cpc_4 = (spend_4 / clicks_4).toFixed(2);
            conversion_4 = ((purchases_4 / clicks_4) * 100).toFixed(0);
        } else {
            cpc_4 = spend_4;
        }
        let cpc_2 = 0;
        let conversion_2 = 0;
        if (clicks_2 > 0) {
            cpc_2 = (spend_2 / clicks_2).toFixed(2);
            conversion_2 = ((purchases_2 / clicks_2) * 100).toFixed(0);
        } else {
            cpc_2 = spend_2;
        }

        let chart_options = {
            maintainAspectRatio: true,
            legend: {
                display: false,
            },
            tooltips: {
                backgroundColor: "#f5f5f5",
                titleFontColor: "#333",
                bodyFontColor: "#666",
                bodySpacing: 4,
                xPadding: 12,
                mode: "nearest",
                intersect: 0,
                position: "nearest",
                callbacks: {
                    label: (tooltipItem, data) => {
                        const label = data.datasets[tooltipItem.datasetIndex].label || '';
                        const value = tooltipItem.yLabel || '';

                        const dateRange = fortnights[tooltipItem.index] || '';
                        return `${label}: ${value} (${dateRange})`;
                    },
                },
            },
            responsive: true,
            scales: {
                yAxes: {
                    barPercentage: 1.6,
                    gridLines: {
                        drawBorder: false,
                        color: "rgba(29,140,248,0.0)",
                        zeroLineColor: "transparent",
                    },
                    ticks: {
                        padding: 20,
                        fontColor: "#9a9a9a",
                    },
                },
                xAxes: {
                    barPercentage: 1.6,
                    gridLines: {
                        drawBorder: false,
                        color: "rgba(29,140,248,0.1)",
                        zeroLineColor: "transparent",
                    },
                    ticks: {
                        padding: 20,
                        fontColor: "#9a9a9a", 
                        // callback: (value, index, values) => {
                        //     return value - fortnights.length; // Count down to -10 as leftmost point
                        // },
                    },
                },
            },
        };


        let impressions_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => item.viewableImpressions) || [],
                        },
                        {
                            label: "Last 8 week Avg.",
                            fill: false,
                            borderColor: "#89949F", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => impressions_8_avg) || [],
                        },
                        {
                            label: "Last 4 week Avg.",
                            fill: false,
                            borderColor: "#5C6670", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => impressions_4_avg) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let clicks_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => item.clicks) || [],
                        },
                        {
                            label: "Last 8 week Avg.",
                            fill: false,
                            borderColor: "#89949F", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => clicks_8_avg) || [],
                        },
                        {
                            label: "Last 4 week Avg.",
                            fill: false,
                            borderColor: "#5C6670", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => clicks_4_avg) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let add_to_cart_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => item.addToCartClicks) || [],
                        },
                        {
                            label: "Last 8 week Avg.",
                            fill: false,
                            borderColor: "#89949F", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => add_to_cart_8_avg) || [],
                        },
                        {
                            label: "Last 4 week Avg.",
                            fill: false,
                            borderColor: "#5C6670", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => add_to_cart_4_avg) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let purchases_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => item.purchasesClicks) || [],
                        },
                        {
                            label: "Last 8 week Avg.",
                            fill: false,
                            borderColor: "#89949F", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => purchases_8_avg) || [],
                        },
                        {
                            label: "Last 4 week Avg.",
                            fill: false,
                            borderColor: "#5C6670", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => purchases_4_avg) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let spend_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => item.cost) || [],
                        },
                        {
                            label: "Last 8 week Avg.",
                            fill: false,
                            borderColor: "#89949F", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => spend_8_avg) || [],
                        },
                        {
                            label: "Last 4 week Avg.",
                            fill: false,
                            borderColor: "#5C6670", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => spend_4_avg) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let CPC_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => {
                                if (item.clicks !== 0) {
                                    return item.cost / item.clicks;
                                } else {
                                    return item.cost;
                                }
                            }) || [],
                        },
                        {
                            label: "Last 8 week Avg.",
                            fill: false,
                            borderColor: "#89949F", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => cpc_8) || [],
                        },
                        {
                            label: "Last 4 week Avg.",
                            fill: false,
                            borderColor: "#5C6670", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => cpc_4) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let spend_per_purchase_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => {
                                if (item.purchasesClicks !== 0) {
                                    return item.cost / item.purchasesClicks;
                                } else {
                                    return item.cost;
                                }
                            }) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };

        let acos_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => {
                                if (item.salesClicks !== 0) {
                                    return (item.cost / item.salesClicks) * 100;
                                } else {
                                    return null;
                                }
                            }) || [],
                        },
                        {
                            label: "Last 8 week Avg.",
                            fill: false,
                            borderColor: "#89949F", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => acos_8) || [],
                        },
                        {
                            label: "Last 4 week Avg.",
                            fill: false,
                            borderColor: "#5C6670", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => acos_4) || [],
                        },
                        {
                            label: "Profitability",
                            fill: false,
                            borderColor: "#a9b665", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => 20) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let sales_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => item.salesClicks) || [],
                        },
                        {
                            label: "Last 8 week Avg.",
                            fill: false,
                            borderColor: "#89949F", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => sales_8_avg) || [],
                        },
                        {
                            label: "Last 4 week Avg.",
                            fill: false,
                            borderColor: "#5C6670", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => sales_4_avg) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let add_to_cart_rate_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => item.addToCartRate) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let conversion_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => {
                                if (item.clicks !== 0) {
                                    return (item.purchasesClicks / item.clicks) * 100;
                                } else {
                                    return null;
                                }
                            }) || [],
                        },
                        {
                            label: "Last 8 week Avg.",
                            fill: false,
                            borderColor: "#89949F", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => conversion_8) || [],
                        },
                        {
                            label: "Last 4 week Avg.",
                            fill: false,
                            borderColor: "#5C6670", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => conversion_4) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };

        let detail_page_views_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => item.detailPageViewsClicks) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let viewability_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => item.viewabilityRate) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let ctr_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => item.viewClickThroughRate) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        return (
            <div>
                <div style={{ marginBottom: '2.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Button
                        variation="primary"
                        colorTheme="warning"
                        onClick={() => navigate('/sb')}
                        style={{ marginRight: 'auto' }}
                    >
                        &lt; Campaign List
                    </Button>
                    <Heading level={1} color="#b9b5b2" style={{ margin: '0 auto' }}>{campaign_name} ({campaignId})</Heading>
                    <Button onClick={handleOpenPopout}>✍️</Button>
                    <SidePopout 
                        company={company}
                        campaigns={[`${campaign_name} - ${campaignId}`]}
                        dates={fortnights}
                        isOpen={isPopoutOpen}
                        onClose={handleClosePopout}
                    />
                </div>
                <Grid
                    columnGap="2rem"
                    rowGap="0.5rem"
                    templateColumns="1fr 3fr"
                    templateRows="1fr"
                >
                    <View>
                        <Grid 
                            columnGap="2rem"
                            rowGap="0.5rem"
                            templateColumns="1fr 1fr"
                            templateRows="1fr"
                        >
                            <View style={{ textAlign: 'left' }}>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>Status:</Heading>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>Type:</Heading>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>Cost Type:</Heading>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>Region:</Heading>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>Budget Amount:</Heading>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>Budget Type:</Heading> 
                            </View>
                            <View style={{ textAlign: 'right' }}>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>{campaign_status}</Heading>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>{campaign_type}</Heading>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>{cost_type}</Heading>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>{campaign_region}</Heading>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>{budget_amount}</Heading>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>{budget_type}</Heading> 
                            </View>
                        </Grid>
                        <Table
                            highlightOnHover={true}
                            className="global-styling-table"
                        >
                        <TableBody>
                            <TableRow>
                              <TableCell as="th" />
                              <TableCell as="th">Last 8 week avg.</TableCell>
                              <TableCell as="th">Last 4 week avg.</TableCell>
                              <TableCell as="th">Last 2 week avg.</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell as="th">ACOS</TableCell>
                              <TableCell>{acos_8} %</TableCell>
                              <TableCell>{acos_4} %</TableCell>
                              <TableCell>{acos_2} %</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell as="th">CPC</TableCell>
                              <TableCell>{cpc_8}</TableCell>
                              <TableCell>{cpc_4}</TableCell>
                              <TableCell>{cpc_2}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell as="th">Spend</TableCell>
                              <TableCell>{spend_8_avg}</TableCell>
                              <TableCell>{spend_4_avg}</TableCell>
                              <TableCell>{spend_2_avg}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell as="th">Sales</TableCell>
                              <TableCell>{sales_8_avg}</TableCell>
                              <TableCell>{sales_4_avg}</TableCell>
                              <TableCell>{sales_2_avg}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell as="th">Impressions</TableCell>
                              <TableCell>{impressions_8_avg}</TableCell>
                              <TableCell>{impressions_4_avg}</TableCell>
                              <TableCell>{impressions_2_avg}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell as="th">Clicks</TableCell>
                              <TableCell>{clicks_8_avg}</TableCell>
                              <TableCell>{clicks_4_avg}</TableCell>
                              <TableCell>{clicks_2_avg}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell as="th">Purchases</TableCell>
                              <TableCell>{purchases_8_avg}</TableCell>
                              <TableCell>{purchases_4_avg}</TableCell>
                              <TableCell>{purchases_2_avg} </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell as="th">Conversion</TableCell>
                              <TableCell>{conversion_8} %</TableCell>
                              <TableCell>{conversion_4} %</TableCell>
                              <TableCell>{conversion_2} %</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                    </View>
                    <View className="graph-container">
                        <Grid
                            columnGap="0.5rem"
                            rowGap="0.5rem"
                            templateColumns="1fr 1fr"
                            templateRows="1fr 1fr 1fr 1fr 1fr"
                        >
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Impressions</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={impressions_graph.data}
                                            options={impressions_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Clicks</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={clicks_graph.data}
                                            options={clicks_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Added To Cart</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={add_to_cart_graph.data}
                                            options={add_to_cart_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Purchases</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={purchases_graph.data}
                                            options={purchases_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">ACOS</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={acos_graph.data}
                                            options={acos_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Conversion Rate</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={conversion_graph.data}
                                            options={conversion_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Spend</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={spend_graph.data}
                                            options={spend_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Sales Revenue</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={sales_graph.data}
                                            options={sales_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Spend Per Click</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={CPC_graph.data}
                                            options={CPC_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Spend Per Purchase</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={spend_per_purchase_graph.data}
                                            options={spend_per_purchase_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Detailed Page Views</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={detail_page_views_graph.data}
                                            options={detail_page_views_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Click Through Rate</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={ctr_graph.data}
                                            options={ctr_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Add to Cart %</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={add_to_cart_rate_graph.data}
                                            options={add_to_cart_rate_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                        </Grid>        
                    </View>
                </Grid>
            </div>
        )
    } else if (campaign_type === "sponsored display") {
        const fortnights = campaignData.map(item => item.date)
        const campaign_name = campaignData[campaignData.length - 1]['campaignName'];
        const campaign_status = campaignData[campaignData.length - 1]['campaignStatus'];
        let campaign_region = "";
        let budget_amount = "";

        if (campaignData[campaignData.length - 1]['campaignBudgetCurrencyCode'] === "EUR") {
            campaign_region = "🇪🇺";
            budget_amount = "€".concat(campaignData[campaignData.length - 1]['campaignBudgetAmount']);
        } else if (campaignData[campaignData.length - 1]['campaignBudgetCurrencyCode'] === "USD") {
            campaign_region = "🇺🇸";
            budget_amount = "$".concat(campaignData[campaignData.length - 1]['campaignBudgetAmount']);
        } else if (campaignData[campaignData.length - 1]['campaignBudgetCurrencyCode'] === "GBP") {
            campaign_region = "🇬🇧";
            budget_amount = "£".concat(campaignData[campaignData.length - 1]['campaignBudgetAmount']);
        }

        const cost_type = campaignData[campaignData.length - 1]['costType'];

        const spend_8 = campaignData.map(item => item.cost).slice(-8).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const spend_8_avg = (spend_8 / 8).toFixed(2);
        const spend_4 = campaignData.map(item => item.cost).slice(-4).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const spend_4_avg = (spend_4 / 4).toFixed(2);
        const spend_2 = campaignData.map(item => item.cost).slice(-2).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const spend_2_avg = (spend_2 / 2).toFixed(2);
        
        const sales_8 = campaignData.map(item => item.salesClicks).slice(-8).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const sales_8_avg = (sales_8 / 8).toFixed(2);
        const sales_4 = campaignData.map(item => item.salesClicks).slice(-4).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const sales_4_avg = (sales_4 / 4).toFixed(2);
        const sales_2 = campaignData.map(item => item.salesClicks).slice(-2).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const sales_2_avg = (sales_2 / 2).toFixed(2);
        
        let acos_8 = 0;
        if (spend_8 > 0 && sales_8 > 0) {
            acos_8 = ((spend_8 / sales_8) * 100).toFixed(2);
        } else if (spend_8 > 0) {
            acos_8 = "> 100";
        }
        let acos_4 = 0;
        if (spend_4 > 0 && sales_4 > 0) {
            acos_4 = ((spend_4 / sales_4) * 100).toFixed(2);
        } else if (spend_4 > 0) {
            acos_4 = "> 100";
        }
        let acos_2 = 0;
        if (spend_2 > 0 && sales_2 > 0) {
            acos_2 = ((spend_2 / sales_2) * 100).toFixed(2);
        } else if (spend_2 > 0) {
            acos_2 = "> 100";
        }

        const impressions_8 = campaignData.map(item => item.impressionsViews).slice(-8).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const impressions_8_avg = (impressions_8 / 8).toFixed(0);
        const impressions_4 = campaignData.map(item => item.impressionsViews).slice(-4).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const impressions_4_avg = (impressions_4 / 4).toFixed(0);
        const impressions_2 = campaignData.map(item => item.impressionsViews).slice(-2).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const impressions_2_avg = (impressions_2 / 2).toFixed(0);
        
        const add_to_cart_8 = campaignData.map(item => item.addToCartClicks).slice(-8).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const add_to_cart_8_avg = (add_to_cart_8 / 8).toFixed(0);
        const add_to_cart_4 = campaignData.map(item => item.addToCartClicks).slice(-4).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const add_to_cart_4_avg = (add_to_cart_4 / 4).toFixed(0);
        const add_to_cart_2 = campaignData.map(item => item.addToCartClicks).slice(-2).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const add_to_cart_2_avg = (add_to_cart_2 / 2).toFixed(0);

        const clicks_8 = campaignData.map(item => item.clicks).slice(-8).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const clicks_8_avg = (clicks_8 / 8).toFixed(0);
        const clicks_4 = campaignData.map(item => item.clicks).slice(-4).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const clicks_4_avg = (clicks_4 / 4).toFixed(0);
        const clicks_2 = campaignData.map(item => item.clicks).slice(-2).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const clicks_2_avg = (clicks_2 / 2).toFixed(0);


        const purchases_8 = campaignData.map(item => item.purchasesClicks).slice(-8).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const purchases_8_avg = (purchases_8 / 8).toFixed(0);
        const purchases_4 = campaignData.map(item => item.purchasesClicks).slice(-4).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const purchases_4_avg = (purchases_4 / 4).toFixed(0);
        const purchases_2 = campaignData.map(item => item.purchasesClicks).slice(-2).reduce((accumulator, current) => {
            return accumulator + current
        }, 0);
        const purchases_2_avg = (purchases_2 / 2).toFixed(0);

        let cpc_8 = 0;
        let conversion_8 = 0;
        if (clicks_8 > 0) {
            cpc_8 = (spend_8 / clicks_8).toFixed(2);
            conversion_8 = ((purchases_8 / clicks_8) * 100).toFixed(0);
        } else {
            cpc_8 = spend_8;
        }
        let cpc_4 = 0;
        let conversion_4 = 0;
        if (clicks_4 > 0) {
            cpc_4 = (spend_4 / clicks_4).toFixed(2);
            conversion_4 = ((purchases_4 / clicks_4) * 100).toFixed(0);
        } else {
            cpc_4 = spend_4;
        }
        let cpc_2 = 0;
        let conversion_2 = 0;
        if (clicks_2 > 0) {
            cpc_2 = (spend_2 / clicks_2).toFixed(2);
            conversion_2 = ((purchases_2 / clicks_2) * 100).toFixed(0);
        } else {
            cpc_2 = spend_2;
        }


        let chart_options = {
            maintainAspectRatio: true,
            legend: {
                display: false,
            },
            tooltips: {
                backgroundColor: "#f5f5f5",
                titleFontColor: "#333",
                bodyFontColor: "#666",
                bodySpacing: 4,
                xPadding: 12,
                mode: "nearest",
                intersect: 0,
                position: "nearest",
                callbacks: {
                    label: (tooltipItem, data) => {
                        const label = data.datasets[tooltipItem.datasetIndex].label || '';
                        const value = tooltipItem.yLabel || '';

                        const dateRange = fortnights[tooltipItem.index] || '';
                        return `${label}: ${value} (${dateRange})`;
                    },
                },
            },
            responsive: true,
            scales: {
                yAxes: {
                    barPercentage: 1.6,
                    gridLines: {
                        drawBorder: false,
                        color: "rgba(29,140,248,0.0)",
                        zeroLineColor: "transparent",
                    },
                    ticks: {
                        padding: 20,
                        fontColor: "#9a9a9a",
                    },
                },
                xAxes: {
                    barPercentage: 1.6,
                    gridLines: {
                        drawBorder: false,
                        color: "rgba(29,140,248,0.1)",
                        zeroLineColor: "transparent",
                    },
                    ticks: {
                        padding: 20,
                        fontColor: "#9a9a9a", 
                        // callback: (value, index, values) => {
                        //     return value - fortnights.length; // Count down to -10 as leftmost point
                        // },
                    },
                },
            },
        };


        let impressions_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => item.impressionsViews) || [],
                        },
                        {
                            label: "Last 8 week Avg.",
                            fill: false,
                            borderColor: "#89949F", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => impressions_8_avg) || [],
                        },
                        {
                            label: "Last 4 week Avg.",
                            fill: false,
                            borderColor: "#5C6670", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => impressions_4_avg) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let clicks_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => item.clicks) || [],
                        },
                        {
                            label: "Last 8 week Avg.",
                            fill: false,
                            borderColor: "#89949F", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => clicks_8_avg) || [],
                        },
                        {
                            label: "Last 4 week Avg.",
                            fill: false,
                            borderColor: "#5C6670", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => clicks_4_avg) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let add_to_cart_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => item.addToCartClicks) || [],
                        },
                        {
                            label: "Last 8 week Avg.",
                            fill: false,
                            borderColor: "#89949F", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => add_to_cart_8_avg) || [],
                        },
                        {
                            label: "Last 4 week Avg.",
                            fill: false,
                            borderColor: "#5C6670", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => add_to_cart_4_avg) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let purchases_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => item.purchasesClicks) || [],
                        },
                        {
                            label: "Last 8 week Avg.",
                            fill: false,
                            borderColor: "#89949F", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => purchases_8_avg) || [],
                        },
                        {
                            label: "Last 4 week Avg.",
                            fill: false,
                            borderColor: "#5C6670", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => purchases_4_avg) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let spend_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => item.cost) || [],
                        },
                        {
                            label: "Last 8 week Avg.",
                            fill: false,
                            borderColor: "#89949F", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => spend_8_avg) || [],
                        },
                        {
                            label: "Last 4 week Avg.",
                            fill: false,
                            borderColor: "#5C6670", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => spend_4_avg) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let CPC_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => {
                                if (item.clicks !== 0) {
                                    return item.cost / item.clicks;
                                } else {
                                    return item.cost;
                                }
                            }) || [],
                        },
                        {
                            label: "Last 8 week Avg.",
                            fill: false,
                            borderColor: "#89949F", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => cpc_8) || [],
                        },
                        {
                            label: "Last 4 week Avg.",
                            fill: false,
                            borderColor: "#5C6670", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => cpc_4) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let spend_per_purchase_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => {
                                if (item.purchasesClicks !== 0) {
                                    return item.cost / item.purchasesClicks;
                                } else {
                                    return item.cost;
                                }
                            }) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };

        let acos_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => {
                                if (item.salesClicks !== 0) {
                                    return (item.cost / item.salesClicks) * 100;
                                } else {
                                    return null;
                                }
                            }) || [],
                        },
                        {
                            label: "Last 8 week Avg.",
                            fill: false,
                            borderColor: "#89949F", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => acos_8) || [],
                        },
                        {
                            label: "Last 4 week Avg.",
                            fill: false,
                            borderColor: "#5C6670", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => acos_4) || [],
                        },
                        {
                            label: "Profitability",
                            fill: false,
                            borderColor: "#a9b665", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => 20) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let sales_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => item.salesClicks) || [],
                        },
                        {
                            label: "Last 8 week Avg.",
                            fill: false,
                            borderColor: "#89949F", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => sales_8_avg) || [],
                        },
                        {
                            label: "Last 4 week Avg.",
                            fill: false,
                            borderColor: "#5C6670", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => sales_4_avg) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let add_to_cart_rate_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => item.addToCartRate) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let conversion_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => {
                                if (item.clicks !== 0) {
                                    return (item.purchasesClicks / item.clicks) * 100;
                                } else {
                                    return null;
                                }
                            }) || [],
                        },
                        {
                            label: "Last 8 week Avg.",
                            fill: false,
                            borderColor: "#89949F", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => conversion_8) || [],
                        },
                        {
                            label: "Last 4 week Avg.",
                            fill: false,
                            borderColor: "#5C6670", // Grey
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 0,
                            data: campaignData.map(item => conversion_4) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let detail_page_views_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => item.detailPageViewsClicks) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let viewability_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => item.viewabilityRate) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        let ctr_graph = {
            data: (canvas) => {
                let ctx = canvas.getContext("2d");

                let redGradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
                redGradientStroke.addColorStop(1, "rgba(255,87,34,0.2)"); // Red
                redGradientStroke.addColorStop(0.4, "rgba(255,87,34,0.0)");
                redGradientStroke.addColorStop(0, "rgba(255,87,34,0)");

                return {
                    labels: fortnights,
                    datasets: [
                        {
                            label: "",
                            fill: true,
                            backgroundColor: redGradientStroke,
                            borderColor: "#FF5722", // Red
                            borderWidth: 2,
                            borderDash: [],
                            borderDashOffset: 0.0,
                            pointBackgroundColor: "#FF5722", // Red
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF5722", // Red
                            pointBorderWidth: 20,
                            pointHoverRadius: 4,
                            pointHoverBorderWidth: 15,
                            // hoverRadius: 10,
                            pointRadius: 4,
                            data: campaignData.map(item => item.viewClickThroughRate) || [],
                        },
                    ],
                };
            },
            options: chart_options,
        };


        return (
            <div>
                <div style={{ marginBottom: '2.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Button
                        variation="primary"
                        colorTheme="warning"
                        onClick={() => navigate('/sd')}
                        style={{ marginRight: 'auto' }}
                    >
                        &lt; Campaign List
                    </Button>
                    <Heading level={1} color="#b9b5b2" style={{ margin: '0 auto' }}>{campaign_name} ({campaignId})</Heading>
                    <Button onClick={handleOpenPopout}>✍️</Button>
                    <SidePopout 
                        company={company}
                        campaigns={[`${campaign_name} - ${campaignId}`]}
                        dates={fortnights}
                        isOpen={isPopoutOpen}
                        onClose={handleClosePopout}
                    />
                </div>
                <Grid
                    columnGap="2rem"
                    rowGap="0.5rem"
                    templateColumns="1fr 3fr"
                    templateRows="1fr"
                >
                    <View>
                        <Grid 
                            columnGap="2rem"
                            rowGap="0.5rem"
                            templateColumns="1fr 1fr"
                            templateRows="1fr"
                        >
                            <View style={{ textAlign: 'left' }}>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>Status:</Heading>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>Type:</Heading>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>Cost Type:</Heading>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>Region:</Heading>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>Budget Amount:</Heading>
                            </View>
                            <View style={{ textAlign: 'right' }}>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>{campaign_status}</Heading>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>{campaign_type}</Heading>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>{cost_type}</Heading>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>{campaign_region}</Heading>
                                <Heading level={4} color="#b9b5b2" style={{ marginBottom: '1rem' }}>{budget_amount}</Heading>
                            </View>
                        </Grid>
                        <Table
                            highlightOnHover={true}
                            className="global-styling-table"
                        >
                        <TableBody>
                            <TableRow>
                              <TableCell as="th" />
                              <TableCell as="th">Last 8 week avg.</TableCell>
                              <TableCell as="th">Last 4 week avg.</TableCell>
                              <TableCell as="th">Last 2 week avg.</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell as="th">ACOS</TableCell>
                              <TableCell>{acos_8} %</TableCell>
                              <TableCell>{acos_4} %</TableCell>
                              <TableCell>{acos_2} %</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell as="th">CPC</TableCell>
                              <TableCell>{cpc_8}</TableCell>
                              <TableCell>{cpc_4}</TableCell>
                              <TableCell>{cpc_2}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell as="th">Spend</TableCell>
                              <TableCell>{spend_8_avg}</TableCell>
                              <TableCell>{spend_4_avg}</TableCell>
                              <TableCell>{spend_2_avg}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell as="th">Sales</TableCell>
                              <TableCell>{sales_8_avg}</TableCell>
                              <TableCell>{sales_4_avg}</TableCell>
                              <TableCell>{sales_2_avg}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell as="th">Impressions</TableCell>
                              <TableCell>{impressions_8_avg}</TableCell>
                              <TableCell>{impressions_4_avg}</TableCell>
                              <TableCell>{impressions_2_avg}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell as="th">Clicks</TableCell>
                              <TableCell>{clicks_8_avg}</TableCell>
                              <TableCell>{clicks_4_avg}</TableCell>
                              <TableCell>{clicks_2_avg}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell as="th">Purchases</TableCell>
                              <TableCell>{purchases_8_avg}</TableCell>
                              <TableCell>{purchases_4_avg}</TableCell>
                              <TableCell>{purchases_2_avg}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell as="th">Conversion</TableCell>
                              <TableCell>{conversion_8} %</TableCell>
                              <TableCell>{conversion_4} %</TableCell>
                              <TableCell>{conversion_2} %</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                    </View>
                    <View className="graph-container">
                        <Grid
                            columnGap="0.5rem"
                            rowGap="0.5rem"
                            templateColumns="1fr 1fr"
                            templateRows="1fr 1fr 1fr 1fr 1fr"
                        >
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Impressions</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={impressions_graph.data}
                                            options={impressions_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Clicks</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={clicks_graph.data}
                                            options={clicks_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Added To Cart</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={add_to_cart_graph.data}
                                            options={add_to_cart_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Purchases</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={purchases_graph.data}
                                            options={purchases_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">ACOS</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={acos_graph.data}
                                            options={acos_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Conversion Rate</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={conversion_graph.data}
                                            options={conversion_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Spend</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={spend_graph.data}
                                            options={spend_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Sales Revenue</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={sales_graph.data}
                                            options={sales_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Spend Per Click</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={CPC_graph.data}
                                            options={CPC_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Spend Per Purchase</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={spend_per_purchase_graph.data}
                                            options={spend_per_purchase_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Detailed Page Views</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={detail_page_views_graph.data}
                                            options={detail_page_views_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Click Through Rate</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={ctr_graph.data}
                                            options={ctr_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                            <Card className="card-chart">
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">Add to Cart %</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={add_to_cart_rate_graph.data}
                                            options={add_to_cart_rate_graph.options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                        </Grid>        
                    </View>
                </Grid>
            </div>
        )
    }
}

export { CampaignPage };
